<template>
    <cod-dialog
        ref="dialog"
        :show.sync="showDialog"
        :drawer="drawer"
        max-width="800px"
    >
        <!-- Header -->
        <div slot="header">
            <div>{{ $t('price_calculations.add') }}</div>
        </div>

        <!-- Content -->
        <div slot="content" class="item-form">
            <div class="d-flex">
                <search-product
                    ref="searchProduct"
                    class="flex-1"
                    :label="$t('general.inventory_item')"
                    :products="products"
                    :disable-quantity="true"
                    :error-messages="form.$errors.product_id"
                    @select="selectProduct($event.product)"
                    @blur="form.validate('product_id');"
                />

                <v-btn
                    class="ml-3"
                    icon
                    @click="drawer = 'product'"
                >
                    <v-icon v-if="!product">fal fa-plus</v-icon>
                    <v-icon v-else>fal fa-pen</v-icon>
                </v-btn>
            </div>

            <v-row class="highlight-section">
                <v-col cols="12" class="pb-1 primary--text">{{ $t('price_calculations.section_by_supplier_invoice') }}</v-col>
                <v-col cols="3">
                    <number-field
                        ref="quantity"
                        v-model="form.quantity_abs"
                        :label="$t('price_calculations.quantity')"
                        :precision="3"
                        dense
                        unsigned
                        use-string
                        :disabled="!form.product_id"
                        :error-messages="form.$errors.quantity"
                        :prefix="parseFloat(invoicePrice) < 0 ? '-' : ''"
                        @blur="form.validate('quantity')"
                        @input="onInput('quantity')"
                        @keyup.enter="focusField('invoicePrice')"
                    />
                </v-col>

                <v-col cols="3">
                    <number-field
                        ref="invoicePrice"
                        v-model="form.invoice_price"
                        :label="$t('price_calculations.invoice_price')"
                        :precision="4"
                        dense
                        unsigned
                        use-string
                        :disabled="!form.product_id"
                        :error-messages="form.$errors.invoice_price"
                        @blur="form.validate('invoice_price')"
                        @input="onInput('invoice_price')"
                        @keyup.enter="focusField('discountPercent')"
                    />
                </v-col>

                <v-col cols="3">
                    <number-field
                        ref="discountPercent"
                        v-model="form.invoice_discount_percent"
                        :label="$t('price_calculations.invoice_discount_percent')"
                        :precision="2"
                        dense
                        unsigned
                        use-string
                        suffix="%"
                        :disabled="!form.product_id"
                        :error-messages="form.$errors.invoice_discount_percent"
                        @blur="form.validate('invoice_discount_percent')"
                        @input="onInput('invoice_discount_percent')"
                        @keyup.enter="focusField('marginPercent')"
                    />
                </v-col>

                <v-col cols="3">
                    <v-text-field
                        :value="itemInternal.getInvoiceValue().toFormat('0.00')"
                        :label="$t('price_calculations.invoice_value')"
                        class="readonly"
                        dense
                        readonly
                        disabled
                    />
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="3">
                    <v-text-field
                        :value="itemInternal.getDependentCosts().toFormat('0.00')"
                        :label="$t('price_calculations.dependent_costs')"
                        class="readonly"
                        dense
                        readonly
                        disabled
                    />
                </v-col>

                <v-col cols="3">
                    <v-text-field
                        :value="itemInternal.getPurchaseValue().toFormat('0.00')"
                        :label="$t('price_calculations.purchase_value')"
                        class="readonly"
                        dense
                        readonly
                        disabled
                    />
                </v-col>

                <v-col cols="3">
                    <v-text-field
                        :value="itemInternal.getPriceDifference().toFormat('0.00')"
                        :label="$t('price_calculations.price_difference')"
                        class="readonly"
                        dense
                        readonly
                        disabled
                    />
                </v-col>

                <v-col cols="3">
                    <v-text-field
                        :value="itemInternal.getSellingValue().toFormat('0.00')"
                        :label="$t('price_calculations.selling_value')"
                        class="readonly"
                        dense
                        readonly
                        disabled
                    />
                </v-col>
            </v-row>

            <v-row class="highlight-section">
                <v-col cols="12" class="pb-1 primary--text">{{ $t('price_calculations.section_price') }}</v-col>
                <v-col cols="3">
                    <v-text-field
                        :value="itemInternal.getPurchasePrice().toRoundedUnit(4)"
                        :label="$t('price_calculations.purchase_price')"
                        class="readonly"
                        dense
                        readonly
                        disabled
                    />
                </v-col>

                <v-col cols="3">
                    <number-field
                        ref="marginPercent"
                        v-model="form.margin_percent"
                        :label="$t('price_calculations.margin_percent')"
                        :precision="2"
                        suffix="%"
                        dense
                        unsigned
                        use-string
                        :disabled="!form.product_id || itemInternal.getInvoiceValue().toUnit() == 0"
                        :error-messages="form.$errors.margin_percent"
                        @blur="form.validate('margin_percent');"
                        @input="onInput('margin_percent')"
                        @keyup.enter="focusField('sellingPrice')"
                    />
                </v-col>

                <v-col cols="3">
                    <number-field
                        ref="sellingPrice"
                        v-model="form.selling_price"
                        :label="$t('price_calculations.selling_price')"
                        :precision="2"
                        dense
                        unsigned
                        use-string
                        :disabled="!form.product_id || itemInternal.getInvoiceValue().toUnit() == 0"
                        :error-messages="form.$errors.selling_price"
                        @blur="form.validate('selling_price');"
                        @input="onInput('selling_price')"
                        @keyup.enter="focusField('sellingPriceAfterTax')"
                    />
                </v-col>

                <v-col cols="3">
                    <number-field
                        ref="sellingPriceAfterTax"
                        v-model="form.selling_price_after_tax"
                        :label="$t('price_calculations.selling_price_after_tax') + ` (${itemInternal.getTaxRate()}%)`"
                        :precision="2"
                        dense
                        unsigned
                        use-string
                        :disabled="!form.product_id || itemInternal.getInvoiceValue().toUnit() == 0"
                        :error-messages="form.$errors.selling_price_after_tax"
                        @blur="form.validate('selling_price_after_tax');"
                        @input="onInput('selling_price_after_tax')"
                        @keyup.enter="save"
                    />
                </v-col>
            </v-row>
        </div>

        <!-- Footer -->
        <template slot="footer">
            <v-btn
                color="primary"
                :disabled="form.$busy"
                :loading="form.$busy"
                class="flex-1"
                @click="save"
            >
                {{ $t('general.save') }}
            </v-btn>
        </template>

        <!-- Drawer -->
        <div slot="drawer" class="drawer">
            <v-toolbar
                class="drawer-header"
                absolute
                dark
                flat
                color="primary"
            >
                <v-btn icon dark @click="drawer = null">
                    <v-icon>fal fa-times</v-icon>
                </v-btn>

                <v-toolbar-title>
                    <span v-if="!product">{{ $t('products.add_product') }}</span>
                    <span v-else-if="product.id">{{ $t('products.edit_product') }}</span>
                    <span v-else>{{ $t('products.copy_product') }}</span>
                </v-toolbar-title>
            </v-toolbar>

            <product-form
                v-if="drawer"
                class="drawer-content"
                mode="inventoryAccounting"
                :product="product"
                :products="products"
                :pricelists="pricelists"
                @save="onProductSaved"
            />
        </div>

    </cod-dialog>
</template>

<script>
import SearchProduct from '@/views/shared/components/SearchProduct';
import NumberField from '@/components/NumberField';
import PriceCalculationItem from '@/models/PriceCalculationItem';
import CodDialog from '@/components/CodDialog';
import ProductForm from '@/views/shared/components/ProductForm';
import HashGen from '@/utilities/HashGen';
import { required, minValue, maxValue } from 'vuelidate/lib/validators';

export default {
    components: {
        SearchProduct,
        NumberField,
        CodDialog,
        ProductForm
    },

    props: {
        show: {
            type: Boolean,
            default: false
        },
        item: {
            type: Object,
            default: () => null
        },
        products: {
            type: Array,
            default: () => []
        },
        pricelists: {
            type: Array,
            default: () => []
        },
        invoicePrice: {
            type: [Number, String],
            default: null
        },
        dependentCosts: {
            type: [Number, String],
            default: null
        },
        invoiceWithTax: {
            type: Boolean,
            default: true
        },
        date: {
            type: String,
            default: null
        },
        invoiceDate: {
            type: String,
            default: null
        }
    },

    data() {
        return {
            showDialog: false,

            form: this.createForm(),
            product: null,
            drawer: null,
        };
    },

    validations() {
        const quantityRule = {}
        if(parseFloat(this.invoicePrice) >= 0) quantityRule.minValue = minValue(0.001)
        if(parseFloat(this.invoicePrice) < 0) quantityRule.maxValue = maxValue(-0.001)

        return {
            form: {
                product_id: { required },
                unit_of_measure: { required },
                quantity: { required, ...quantityRule },
                invoice_price: { required, minValue: minValue(0.01) },
                invoice_discount_percent: { required },
                dependent_costs: { required },
                margin_percent: { required, minValue: minValue(0) },
                selling_price: { required, minValue: minValue(this.itemInternal.getPurchasePrice().toRoundedUnit(2)) },
                selling_price_after_tax: { required, minValue: minValue(this.itemInternal.getPurchasePriceAfterTax().toRoundedUnit(2)) },
            }
        }
    },

    watch: {
        'form.quantity_abs'(val) {
            this.form.quantity = !val || parseFloat(this.invoicePrice) >= 0
                ? val
                : Money.parse(val).multiply(-1).toFormat('0.0000')
        },

        show(val) {
            this.showDialog = val;

            if (val) {
                this.drawer = null;
                this.form = this.createForm(this.item);

                setTimeout(() => {
                    const product = this.item?.product_id ? this.products.find(p => p.id === this.item.product_id) : null;
                    this.product = product;

                    if(this.$refs.searchProduct) {
                        if(product) {
                            this.$refs.searchProduct.setSelected(product);
                        } else {
                            this.$refs.searchProduct.focus();
                        }
                    }
                }, 200)
            }
        },

        showDialog(val) {
            this.$emit('update:show', val);
        },
    },

    computed: {
        itemInternal() {
            return new PriceCalculationItem(this.form.toJson());
        }
    },

    methods: {
        createForm(item) {
            return new Form(this, 'form', {
                id: item?.id || null,
                hash: item?.hash || HashGen.regular(),
                date: this.date,
                invoice_date: this.invoiceDate,
                price_calculation_id: item?.price_calculation_id || null,
                product_id: item?.product_id || null,
                tax_rate: item?.tax_rate || null,
                unit_of_measure: item?.unit_of_measure || 'pcs',
                quantity: item?.quantity || (parseFloat(this.invoicePrice) >= 0 ? '1.000' : '-1.000'),
                invoice_price: item?.invoice_price || '0.00',
                invoice_discount_percent: item?.invoice_discount_percent || '0.00',
                invoice_with_tax: item ? item.invoice_with_tax : this.invoiceWithTax,
                dependent_costs: item?.dependent_costs || '0.00',
                margin_percent: item?.margin_percent || '0.00',
                selling_price: item?.selling_price || '0.00',
                selling_price_after_tax: item?.selling_price_after_tax || '0.00',
                quantity_abs: item
                    ? (parseFloat(this.invoicePrice) >= 0 ? item.quantity : Money.parse(item.quantity).multiply(-1).toFormat('0.0000'))
                    : '1.000',
            });
        },

        save() {
            if(!this.form.validate()) return;
            this.form.busy = true;

            this.$emit('save', this.itemInternal);

            setTimeout(() => {
                this.form.busy = false;
                this.showDialog = false;
            }, 200)
        },

        selectProduct(product) {
            const taxRate = product?.getTaxRate();
            if(product && !taxRate) return;

            this.product = product;

            this.form.product_id = product?.id || null;
            this.form.unit_of_measure = product?.unit_of_measure_sold || null;
            this.form.tax_rate = product
                ? (this.tenant.venue.taxes_enabled ? taxRate.rate : '0.00')
                : null;

            if(product) {
                const reverseCalculation = !!this.tenant.venue.settings.reverse_calculation;
                if(reverseCalculation) {
                    this.form.selling_price = product.getPriceBeforeTax().toFormat('0.0000');
                    this.onInput('selling_price');
                } else {
                    this.form.selling_price_after_tax = product.getPriceAfterTax().toFormat('0.0000');
                    this.onInput('selling_price_after_tax');
                }
            } else {
                this.form.selling_price = this.form.selling_price_after_tax = '0';
            }

            this.form.validate('product_id');
            this.$nextTick(() => this.$refs.quantity.focus())
        },

        onInput(field) {
            delete this.form.$errors[field];

            setTimeout(() => {
                if(['quantity', 'invoice_price', 'invoice_discount_percent'].includes(field)) {
                    this.form.dependent_costs = this.itemInternal.calculateDependentCosts(this.invoicePrice, this.dependentCosts);
                    if(this.itemInternal.getInvoiceValue().toUnit() !== 0) {
                        const reverseCalculation = !!this.tenant.venue.settings.reverse_calculation;
                        this.onInput(reverseCalculation ? 'selling_price' : 'selling_price_after_tax')
                    }
                    return;
                }

                if(['selling_price', 'selling_price_after_tax', 'margin_percent'].includes(field)) {
                    const prices = this.itemInternal.calculatePrices(this.form[field], field);

                    this.form.selling_price = prices.selling_price;
                    this.form.selling_price_after_tax = prices.selling_price_after_tax;
                    this.form.margin_percent = prices.margin_percent;

                    this.form.validate('selling_price');
                    this.form.validate('selling_price_after_tax');
                    this.form.validate('margin_percent');
                }
            }, 50)
        },

        /**
         * On product saved handle the updated data and close the form
         * @param data
         */
        onProductSaved(data) {
            this.drawer = null;

            const productsIndex = this.products.findIndex(item => item.id === data.product.id);
            if(productsIndex != -1) {
                this.$set(this.products, productsIndex, data.product);
            } else {
                this.products.unshift(data.product);
            }

            this.selectProduct(data.product);
            if(data.product) {
                this.$refs.searchProduct.setSelected(data.product);
            }
        },

        /**
         * Move focus to next field
         */
        focusField(nextField) {
            if(!this.$refs[nextField]) return;
            this.$refs[nextField].focus();
        }
    }
};
</script>

<style lang="scss" scoped>
.item-form {
    padding: 24px;
    min-height: 575px;

    .highlight-section {
        background: #fafafa;
        margin: 16px -8px 30px -8px;
        border-top: 1px solid #b8b8b8;
        border-bottom:1px solid #b8b8b8;
    }

    .readonly {
        ::v-deep .v-label {
            color: var(--v-primary-base);
        }

        ::v-deep input {
            color: var(--v-primary-base);
        }
    }
}

.drawer {
    .drawer-header {
        height: 64px;
        width: 100%;
    }

    .drawer-content {
        margin-top: 60px;
        padding-left: 24px;
        padding-right: 24px;
    }
}
</style>
